import React, { useState, useEffect } from 'react';
import { searchCustomers, getCustomerAccountBalances, uploadPaymentProof, createPaymentRequest } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { clampDecimals, sanitizeAmountInput, amountInputGuard, compressImage, formatCurrency, customerBalanceSign } from '../utils/helpers';
import ImageUploadBox from './ImageUploadBox';
import './CreatePRModal.css';

// One payment entry: payee account details + amount + compulsory proof
// screenshot. Sales can add several in one go — each becomes its own
// payment_request task that finance claims and pays independently.
const emptyPayment = () => ({ account_details: '', amount: '', proofUrl: '' });

// Sales-initiated "Request Payment": pay out to a customer's designated bank
// account(s). Draws on the customer relationship (balance shown as context —
// NOT a cap; the operation fronts payments). No order involved.
// `initialCustomer` (optional, 2026-10-17): open the modal already on that
// customer — the Create Order success panel's "Request Payment for this
// customer" shortcut after a Buy (we owe the customer MYR once the crypto is
// linked, and a regular customer can never go negative, so the order had to
// come first). The cover confirmation was already given on the order, so a
// colleague's customer is prefilled without asking again (the PIC badge and
// the server-side PIC alert still apply).
function RequestPaymentModal({ isOpen, onClose, onSuccess, user, initialCustomer = null }) {
  // HOLIDAY COVER (operator 2026-09-04): the picker searches EVERY customer;
  // own vs. a colleague's is badged, and choosing a colleague's confirms.
  const isMyCustomer = (c) => !c || user?.role !== 'sales' || Number(c.assigned_to) === Number(user?.id);
  const [query, setQuery] = useState('');
  // Optional reference / remark (operator 2026-10-10) — one per request, shown
  // on the task cards and on the payout's history row.
  const [remark, setRemark] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [customer, setCustomer] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [myrBalance, setMyrBalance] = useState(0); // signed: > 0 customer owes us
  const [payments, setPayments] = useState([emptyPayment()]);
  const [uploading, setUploading] = useState(false); // overlay-paste upload in flight
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dupWarnings, setDupWarnings] = useState(null); // array of {rule,message} | null

  useEffect(() => {
    if (isOpen) {
      setQuery(''); setResults([]); setCustomer(null); setMyrBalance(0);
      setPayments([emptyPayment()]);
      setErrors({});
      if (initialCustomer && initialCustomer.id) selectCustomer(initialCustomer, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Type-ahead customer search (min 2 chars, lightly debounced).
  useEffect(() => {
    if (!isOpen || customer) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchCustomers(q, { scope: 'all' });
        if (active) { setResults(Array.isArray(data) ? data : []); setSearchedQuery(q); }
      } catch (e) {
        if (active) { setResults([]); setSearchedQuery(q); }
      } finally {
        if (active) setSearching(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, isOpen, customer]);

  // Balance is CONTEXT, not a cap: the operation fronts payments, so a
  // customer often owes us (positive balance) while requesting further
  // payouts — each fulfilled payout increases what they owe.
  const balanceInfo = (() => {
    const v = parseFloat(myrBalance || 0);
    const { sign, label } = customerBalanceSign(v);
    if (sign === 'owes') return { text: `${label}: ${formatCurrency(v)} — this payment will increase it`, color: '#b45309' };
    if (sign === 'credit') return { text: `${label}: ${formatCurrency(-v)}`, color: '#16a34a' };
    return { text: `Balance: ${formatCurrency(0)} · ${label}`, color: '#6b7280' };
  })();

  const selectCustomer = async (c, { silent = false } = {}) => {
    if (!silent && !isMyCustomer(c)) {
      const pic = c.assigned_to_name || 'another sales user';
      if (!window.confirm(`${c.customer_name} is ${pic}'s customer.\n\nYou are requesting a payout on their behalf (e.g. holiday cover); ${pic} will be notified.\n\nContinue with this customer?`)) return;
    }
    setCustomer(c);
    setResults([]);
    setQuery('');
    setBalanceLoading(true);
    try {
      const res = await getCustomerAccountBalances(c.id);
      const rows = res?.data || [];
      const myr = rows.find((b) => b.currency_type === 'MYR');
      setMyrBalance(myr ? parseFloat(myr.balance) : 0);
    } catch (e) {
      setMyrBalance(0);
      showError('Failed to load customer balance');
    } finally {
      setBalanceLoading(false);
    }
  };

  const clearCustomer = () => {
    setCustomer(null); setMyrBalance(0); setPayments([emptyPayment()]); setErrors({});
  };

  const updatePayment = (i, patch) => setPayments((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addPayment = () => setPayments((prev) => [...prev, emptyPayment()]);
  const removePayment = (i) => setPayments((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  // Paste a screenshot anywhere in the modal — attaches to the first payment
  // entry that doesn't have a proof yet.
  const handleOverlayPaste = (e) => {
    if (!customer || submitting || uploading) return;
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        const target = payments.findIndex((p) => !p.proofUrl);
        if (target === -1) return;
        (async () => {
          if (!file.type || !file.type.startsWith('image/')) { showError('Please paste an image'); return; }
          setUploading(true);
          try {
            // On-device compression before upload (house rule — this paste
            // path was the ONE uploader in the app sending the raw file).
            // Default preset, matching the ImageUploadBox below; fall back to
            // the original if compression fails, then re-apply the size cap.
            let uploadFile = file;
            try {
              const { file: compressed } = await compressImage(file, {});
              uploadFile = compressed;
            } catch (compErr) {
              console.warn('Image compression failed; uploading original:', compErr);
            }
            if (uploadFile.size > 10 * 1024 * 1024) { showError('Image must be under 10MB even after compression'); return; }
            const res = await uploadPaymentProof(uploadFile);
            updatePayment(target, { proofUrl: res.url });
          } catch (err) {
            showError(err.response?.data?.error || 'Failed to upload proof image');
          } finally {
            setUploading(false);
          }
        })();
        break;
      }
    }
  };

  const validate = () => {
    const e = {};
    if (!customer) e.customer = 'Select a customer';
    payments.forEach((p, i) => {
      if (!p.account_details.trim()) e[`acc${i}`] = 'Enter the payee bank account details';
      const amt = parseFloat(p.amount);
      if (!p.amount || isNaN(amt) || amt <= 0) e[`amt${i}`] = 'Enter a valid amount';
      if (!p.proofUrl) e[`proof${i}`] = 'A payment proof screenshot is required';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitRequest = async (confirmDuplicate) => {
    setSubmitting(true);
    try {
      await createPaymentRequest({
        customer_id: customer.id,
        confirm_duplicate: confirmDuplicate === true,
        remark: remark.trim(),
        requests: payments.map((p) => ({
          account_details: p.account_details.trim(),
          amount: parseFloat(p.amount),
          request_proof_url: p.proofUrl,
        })),
      });
      showSuccess(payments.length === 1 ? 'Payment request sent to finance' : `${payments.length} payment requests sent to finance`);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 400);
    } catch (err) {
      const resp = err.response;
      // Server-enforced duplicate failsafe: show the warnings and let the
      // operator explicitly confirm before we re-submit with confirm_duplicate.
      if (resp?.status === 409 && resp.data?.duplicate) {
        setDupWarnings(resp.data.warnings || []);
        return;
      }
      const msg = resp?.data?.error || 'Failed to send payment request';
      showError(msg);
      setErrors((prev) => ({ ...prev, submit: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setDupWarnings(null);
    submitRequest(false); // first attempt: NOT confirmed — server may 409
  };

  const handleClose = () => { if (!submitting) onClose(); };

  if (!isOpen) return null;

  const totalAmount = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  return (
    // Deliberately NO close-on-overlay-click: a mis-click outside the box must
    // not throw away a half-filled request. Close via the × or Cancel buttons.
    <div className="modal-overlay" onPaste={handleOverlayPaste}>
      <div className="modal-content create-pr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3>Request Payment</h3>
          <button onClick={handleClose} className="close-button" disabled={submitting}>&times;</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Customer selection */}
            {!customer ? (
              <div className="form-group">
                <label className="form-label">Customer <span className="required">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.customer ? 'error' : ''}`}
                  placeholder="Search by name, number or nickname…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {errors.customer && <span className="error-message">{errors.customer}</span>}
                {searching && <small className="form-text">Searching…</small>}
                {!searching && query.trim().length >= 2 && searchedQuery === query.trim() && results.length === 0 && (
                  <small className="form-text" style={{ color: '#b45309' }}>No customer matches “{query.trim()}” — check the spelling, or create the profile in Customer Management first.</small>
                )}
                {results.length > 0 && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                    {results.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => selectCustomer(c)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                          ...(user?.role === 'sales' ? (isMyCustomer(c) ? { borderLeft: '4px solid #16a34a' } : { borderLeft: '4px solid #f59e0b', background: '#fffbeb' }) : {}) }}
                      >
                        <strong>{c.customer_name}</strong>
                        {c.customer_nickname ? ` (${c.customer_nickname})` : ''} — {c.customer_number}
                        {user?.role === 'sales' && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                            color: isMyCustomer(c) ? '#166534' : '#92400e', background: isMyCustomer(c) ? '#dcfce7' : '#fef3c7' }}>
                            {isMyCustomer(c) ? 'Your customer' : `PIC: ${c.assigned_to_name || 'another sales'}`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Customer</label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                  <div>
                    <strong>{customer.customer_name}</strong>
                    {customer.customer_nickname ? ` (${customer.customer_nickname})` : ''} — {customer.customer_number}
                    <div style={{ marginTop: 4, fontSize: 13 }}>
                      {balanceLoading ? 'Loading balance…' : (
                        <span style={{ color: balanceInfo.color, fontWeight: 600 }}>{balanceInfo.text}</span>
                      )}
                    </div>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={clearCustomer} disabled={submitting} style={{ padding: '2px 10px' }}>Change</button>
                </div>
              </div>
            )}

            {/* Payment entries (once a customer is picked) */}
            {customer && (
              <>
                <div className="form-group">
                  <label className="form-label">Reference / remarks <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={remark}
                    maxLength={500}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="e.g. invoice no., what this payout is for — finance sees it on the task and in history"
                    disabled={submitting}
                  />
                </div>
                {payments.map((p, i) => (
                  <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong>Payment #{i + 1}</strong>
                      {payments.length > 1 && (
                        <button type="button" className="btn btn-secondary" onClick={() => removePayment(i)} disabled={submitting} style={{ padding: '2px 10px' }}>
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Payment account details <span className="required">*</span></label>
                      <textarea
                        className={`form-control ${errors[`acc${i}`] ? 'error' : ''}`}
                        rows={3}
                        placeholder={'e.g.\nGD THEENA RATCHAGAN\nMBB\n514347053065'}
                        value={p.account_details}
                        onChange={(e) => updatePayment(i, { account_details: e.target.value })}
                        disabled={submitting}
                      />
                      {errors[`acc${i}`] && <span className="error-message">{errors[`acc${i}`]}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Amount (MYR) <span className="required">*</span></label>
                      <input
                        type="number"
                        className={`form-control ${errors[`amt${i}`] ? 'error' : ''}`}
                        step="0.01" min="0.01"
                        placeholder="0.00"
                        value={p.amount}
                        onChange={(e) => updatePayment(i, { amount: clampDecimals(sanitizeAmountInput(e.target.value), 2) })}
                        {...amountInputGuard}
                        disabled={submitting}
                      />
                      {errors[`amt${i}`] && <span className="error-message">{errors[`amt${i}`]}</span>}
                    </div>

                    <ImageUploadBox
                      endpoint="/upload/payment-proof"
                      label="Proof screenshot (customer's bank details)"
                      required
                      currentUrl={p.proofUrl}
                      onUploadSuccess={(url) => updatePayment(i, { proofUrl: url })}
                      helperText="Click, drag-drop, or paste (Ctrl/Cmd+V) — pasting anywhere in this window fills the first payment without a proof"
                    />
                    {errors[`proof${i}`] && <span className="error-message" style={{ display: 'block', marginTop: -8, marginBottom: 4 }}>{errors[`proof${i}`]}</span>}
                  </div>
                ))}

                <button type="button" className="btn btn-secondary" onClick={addPayment} disabled={submitting} style={{ marginBottom: 12 }}>
                  + Add another payment
                </button>

                {uploading && <small className="form-text" style={{ display: 'block' }}>Uploading pasted screenshot…</small>}

                {payments.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: 12 }}>
                    <span>Total to request:</span>
                    <span>{formatCurrency(totalAmount)}</span>
                  </div>
                )}

                {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

                {/* Server-enforced duplicate failsafe: show the warnings and make
                    the operator explicitly confirm before we resubmit. */}
                {dupWarnings && dupWarnings.length > 0 && (
                  <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <strong style={{ display: 'block', marginBottom: 6 }}>⚠ Possible duplicate — please confirm</strong>
                    <ul style={{ margin: '0 0 8px', paddingLeft: 18, lineHeight: 1.6 }}>
                      {dupWarnings.map((w, i) => (
                        <li key={i} style={{ color: w.rule === 'PAYEE_OTHER_CUSTOMER' ? '#b91c1c' : '#92400e', fontWeight: w.rule === 'PAYEE_OTHER_CUSTOMER' ? 600 : 400 }}>
                          {w.message}
                        </li>
                      ))}
                    </ul>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="btn btn-secondary" disabled={submitting} onClick={() => setDupWarnings(null)}>Cancel</button>
                      <button type="button" className="btn btn-warning" disabled={submitting} onClick={() => submitRequest(true)}>
                        {submitting ? 'Sending…' : 'Yes, proceed anyway'}
                      </button>
                    </div>
                  </div>
                )}

                {!dupWarnings && (
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                      {submitting ? 'Sending…' : `Send request${payments.length > 1 ? 's' : ''} to finance`}
                    </button>
                    <button type="button" onClick={handleClose} className="btn btn-secondary" disabled={submitting}>Cancel</button>
                  </div>
                )}
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default RequestPaymentModal;
